import { Transaction, PaydayConfig, PayFrequency } from './types';

export interface PaydayInfo {
  nextPayday: Date;
  daysUntil: number;
  estimatedAmount: number;
  frequency: PayFrequency;
  isManual: boolean;
}

/**
 * Get payday info from manual config or auto-detect from transactions.
 */
export function getPaydayInfo(
  transactions: Transaction[],
  config?: PaydayConfig,
): PaydayInfo | null {
  if (config?.mode === 'manual' && config.frequency && config.nextDate) {
    return getManualPayday(config);
  }
  return detectPayday(transactions);
}

function getManualPayday(config: PaydayConfig): PaydayInfo | null {
  const frequency = config.frequency!;
  const now = new Date();
  let nextPayday = new Date(config.nextDate!);

  const intervalDays = frequency === 'weekly' ? 7 : frequency === 'fortnightly' ? 14 : 30;

  // Advance past dates
  if (frequency === 'monthly') {
    const dayOfMonth = nextPayday.getDate();
    nextPayday = new Date(now.getFullYear(), now.getMonth(), dayOfMonth);
    if (nextPayday <= now) nextPayday.setMonth(nextPayday.getMonth() + 1);
  } else {
    while (nextPayday <= now) {
      nextPayday.setDate(nextPayday.getDate() + intervalDays);
    }
  }

  const daysUntil = Math.ceil((nextPayday.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  return {
    nextPayday,
    daysUntil,
    estimatedAmount: config.amount ?? 0,
    frequency,
    isManual: true,
  };
}

/**
 * Auto-detect pay frequency and next payday from income transactions.
 */
export function detectPayday(transactions: Transaction[]): PaydayInfo | null {
  const now = new Date();
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - 90);

  const incomes = transactions
    .filter(t => t.type === 'income' && t.date >= cutoff.toISOString().slice(0, 10))
    .sort((a, b) => a.date.localeCompare(b.date));

  if (incomes.length < 2) return null;

  const gaps: number[] = [];
  for (let i = 1; i < incomes.length; i++) {
    const prev = new Date(incomes[i - 1].date);
    const curr = new Date(incomes[i].date);
    const diffDays = Math.round((curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > 0) gaps.push(diffDays);
  }

  if (gaps.length === 0) return null;

  const avgGap = gaps.reduce((s, g) => s + g, 0) / gaps.length;

  let frequency: PayFrequency;
  let intervalDays: number;
  if (avgGap <= 10) {
    frequency = 'weekly';
    intervalDays = 7;
  } else if (avgGap <= 20) {
    frequency = 'fortnightly';
    intervalDays = 14;
  } else {
    frequency = 'monthly';
    intervalDays = 30;
  }

  const lastPayDate = new Date(incomes[incomes.length - 1].date);
  let nextPayday = new Date(lastPayDate);

  if (frequency === 'monthly') {
    const payDayOfMonth = lastPayDate.getDate();
    nextPayday = new Date(now.getFullYear(), now.getMonth(), payDayOfMonth);
    if (nextPayday <= now) nextPayday.setMonth(nextPayday.getMonth() + 1);
  } else {
    while (nextPayday <= now) {
      nextPayday.setDate(nextPayday.getDate() + intervalDays);
    }
  }

  const daysUntil = Math.ceil((nextPayday.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const recentIncomes = incomes.slice(-6);
  const estimatedAmount = recentIncomes.reduce((s, t) => s + t.amount, 0) / recentIncomes.length;

  return { nextPayday, daysUntil, estimatedAmount, frequency, isManual: false };
}
